import React, { FC, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { Paper } from '@mui/material';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { NotificationTemplate } from 'models/restTypes';

const StyledGrid = styled(Grid)`
  display: flex;
  flex: unset;
  height: 100%;
  width: 100%;
  align-items: normal;
`;

const DataTableContainer = styled(Paper)`
  overflow: hidden;
  height: 100%;
  background-color: var(--surface-ground);
`;

const NotificationTemplateTextTable: FC<{
  templates: NotificationTemplate[];
  isLoading: boolean;
}> = observer(({ templates, isLoading }) => {
  const { t } = useTranslation('staticData');

  const [filters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    notificationTypeId: { value: '', matchMode: FilterMatchMode.CONTAINS },
    channel: { value: '', matchMode: FilterMatchMode.CONTAINS },
    providerName: { value: '', matchMode: FilterMatchMode.CONTAINS },
    templateName: { value: '', matchMode: FilterMatchMode.CONTAINS },
    language: { value: '', matchMode: FilterMatchMode.CONTAINS },
    templateId: { value: '', matchMode: FilterMatchMode.CONTAINS },
    templateSource: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowClick = useCallback(({ data }: DataTableRowClickEvent) => {
    // Handle row click
    // eslint-disable-next-line no-console
    console.log(data);
  }, []);

  const templateTextBody = useCallback((rowData: NotificationTemplate) => {
    if (!rowData.templateText) return '-';
    return rowData.templateText.length > 50
      ? `${rowData.templateText.substring(0, 50)}...`
      : rowData.templateText;
  }, []);

  const imageUriBody = useCallback((rowData: NotificationTemplate) => {
    if (!rowData.imageUri) return '-';
    return (
      <a href={rowData.imageUri} target="_blank" rel="noopener noreferrer">
        View Image
      </a>
    );
  }, []);

  const providerTemplateNameBody = useCallback(
    (rowData: NotificationTemplate) => {
      return rowData.providerTemplateName || '-';
    },
    [],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledGrid flex={1}>
      <DataTableContainer elevation={3}>
        <DataTable
          id="NotificationTemplateTextTable"
          showGridlines
          resizableColumns
          value={templates}
          selectionMode="single"
          scrollable
          scrollHeight="flex"
          dataKey="templateId"
          emptyMessage={t('empty_message')}
          stripedRows
          filters={filters}
          onRowClick={onRowClick}
          filterDisplay="row"
          globalFilterFields={[
            'notificationTypeId',
            'channel',
            'providerName',
            'templateName',
            'language',
            'templateId',
            'templateSource',
          ]}
          paginator
          rows={10}
        >
          <Column
            field="notificationTypeId"
            header={t('notification_type_id')}
            sortable
            filter
          />
          <Column field="channel" header={t('channel')} sortable filter />
          <Column
            field="providerName"
            header={t('provider_name')}
            sortable
            filter
          />
          <Column
            field="templateName"
            header={t('template_name')}
            sortable
            filter
          />
          <Column field="language" header={t('language')} sortable filter />
          <Column
            field="templateId"
            header={t('template_id')}
            sortable
            filter
          />
          <Column
            field="templateSource"
            header={t('template_source')}
            sortable
            filter
          />
          <Column
            field="templateText"
            header={t('template_text')}
            sortable
            body={templateTextBody}
            style={{ maxWidth: '300px' }}
          />
          <Column
            field="imageUri"
            header={t('image_uri')}
            sortable
            body={imageUriBody}
          />
          <Column
            field="providerTemplateName"
            header={t('provider_template_name')}
            sortable
            body={providerTemplateNameBody}
          />
        </DataTable>
      </DataTableContainer>
    </StyledGrid>
  );
});

export default NotificationTemplateTextTable;
